import React, { useEffect, useState } from "react";
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { selectUser } from "../../store/slice/user";
import { COLORS } from "../../theme";
import SubPageNav from "../../components/SubPageNav";
import SiteFooter from "../../components/SiteFooter";
import { getStaticPage } from "../../services/data/staticPages";
import { Style } from "./style";

// Content is backend-driven (admin "Static Pages" > Pricing) with the
// original hardcoded plans/FAQ kept as a fallback, so the page never
// breaks/flashes empty while the backend content is being filled in.
const Pricing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { language } = useSelector(selectUser);
  const isArabic = language === "ar";

  const [apiData, setApiData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getStaticPage("pricing")
      .then((res) => {
        if (!cancelled) setApiData(res.data.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const tr = (item, field) =>
    item?.translation?.[language]?.[field] || item?.translation?.en?.[field] || null;

  const heroItem = apiData?.hero?.[0];
  const heroTitle = tr(heroItem, "title") || t("marketing.pricing.title");
  const heroSubtitle = tr(heroItem, "subtitle") || t("marketing.pricing.subtitle");

  const starterFeatures = [
    "upTo100Tickets",
    "platformFee5",
    "basicAnalytics",
    "emailSupport",
    "mobileAppAccess",
    "standardTicketTypes",
  ];
  const professionalFeatures = [
    "unlimitedTickets",
    "platformFee35",
    "advancedAnalytics",
    "prioritySupport",
    "customBranding",
    "multipleTicketTypes",
    "discountCodes",
    "attendeeManagementTools",
  ];
  const enterpriseFeatures = [
    "everythingInProfessional",
    "negotiatedFees",
    "dedicatedAccountManager",
    "customIntegrations",
    "whiteLabelOptions",
    "apiAccess",
    "phoneSupport247",
    "customContractTerms",
  ];

  const hardcodedPlans = [
    {
      key: "starter",
      name: t("marketing.pricing.starter"),
      price: t("event_detail_page.free"),
      desc: t("marketing.pricing.starterDesc"),
      features: starterFeatures.map((fKey) => t(`marketing.pricing.${fKey}`)),
      cta: t("marketing.pricing.getStarted"),
      featured: false,
    },
    {
      key: "professional",
      name: t("marketing.pricing.professional"),
      price: t("marketing.pricing.price49"),
      perMonth: t("marketing.pricing.perMonth"),
      desc: t("marketing.pricing.professionalDesc"),
      features: professionalFeatures.map((fKey) => t(`marketing.pricing.${fKey}`)),
      cta: t("marketing.pricing.startFreeTrial"),
      featured: true,
    },
    {
      key: "enterprise",
      name: t("marketing.pricing.enterprise"),
      price: t("marketing.pricing.custom"),
      desc: t("marketing.pricing.enterpriseDesc"),
      features: enterpriseFeatures.map((fKey) => t(`marketing.pricing.${fKey}`)),
      cta: t("marketing.nav.contactSales"),
      featured: false,
    },
  ];
  const plans =
    apiData?.plan?.length
      ? apiData.plan.map((p) => ({
          key: p.id,
          name: tr(p, "title"),
          price: tr(p, "price"),
          perMonth: tr(p, "subtitle"),
          desc: tr(p, "body"),
          features: (tr(p, "meta_text") || "").split("\n").map((f) => f.trim()).filter(Boolean),
          cta: tr(p, "button_label"),
          featured: !!p.meta?.featured,
        }))
      : hardcodedPlans;

  const hardcodedFaqItems = [
    "faqPlatformFee",
    "faqPaid",
    "faqUpgrade",
    "faqSetupFees",
    "faqPaymentMethods",
  ].map((qKey) => ({
    key: qKey,
    question: t(`marketing.pricing.${qKey}Q`),
    answer: t(`marketing.pricing.${qKey}A`),
  }));
  const faqItems =
    apiData?.faq?.length
      ? apiData.faq.map((f) => ({ key: f.id, question: tr(f, "title"), answer: tr(f, "body") }))
      : hardcodedFaqItems;

  return (
    <Box sx={Style.page}>
      <SubPageNav />
      <Box sx={Style.hero}>
        <Typography component="h1" sx={Style.heroTitle(isArabic)}>
          {heroTitle}
        </Typography>
        <Typography sx={Style.heroSubtitle(isArabic)}>
          {heroSubtitle}
        </Typography>
      </Box>

      <Box sx={Style.grid}>
        {plans.map((plan) => (
          <Box key={plan.key} sx={Style.card(plan.featured)}>
            {plan.featured && (
              <Box sx={Style.popularBadge(isArabic)}>
                {t("marketing.pricing.mostPopular")}
              </Box>
            )}
            <Typography sx={Style.planName(isArabic)}>{plan.name}</Typography>
            <Typography sx={Style.planPrice(isArabic)}>
              {plan.price}
              {plan.perMonth && (
                <Typography component="span" sx={Style.perMonth}>
                  {plan.perMonth}
                </Typography>
              )}
            </Typography>
            <Typography sx={Style.planDesc(isArabic)}>{plan.desc}</Typography>
            <Box component="ul" sx={Style.featureList}>
              {plan.features.map((feature, i) => (
                <Box component="li" key={i} sx={Style.featureItem(isArabic)}>
                  <Box component="span" sx={Style.check}>✓</Box>
                  {feature}
                </Box>
              ))}
            </Box>
            <Box
              component="button"
              onClick={() => navigate("/contact-sales")}
              sx={Style.planButton(isArabic, plan.featured)}
            >
              {plan.cta}
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={Style.faq}>
        <Typography sx={Style.faqTitle(isArabic)}>
          {t("marketing.pricing.faqTitle")}
        </Typography>
        {faqItems.map((faq) => (
          <Accordion key={faq.key} sx={Style.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: COLORS.peach }} />}>
              <Typography sx={Style.accordionQuestion(isArabic)}>
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={Style.accordionAnswer(isArabic)}>
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
      <SiteFooter />
    </Box>
  );
};

export default Pricing;
